import { useEffect, useRef } from "react";

interface HtmlReportShadowProps {
  html: string;
}

export function HtmlReportShadow({ html }: HtmlReportShadowProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let shadow = container.shadowRoot;
    if (!shadow) {
      shadow = container.attachShadow({ mode: "open" });
    }

    const updateTheme = () => {
      const isDark = document.documentElement.classList.contains("dark");
      if (isDark) {
        container.setAttribute("data-theme", "dark");
      } else {
        container.removeAttribute("data-theme");
      }
      const rootDiv = shadow?.querySelector(".report-root");
      if (rootDiv) {
        rootDiv.className = isDark
          ? "report-root dark report-theme-dark"
          : "report-root";
      }
    };

    // Parse the complete HTML document
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Clear previous content
    shadow.innerHTML = "";

    // Copy font links and external stylesheets into shadow root
    for (const linkEl of doc.head.querySelectorAll("link")) {
      shadow.appendChild(linkEl.cloneNode(true));
    }

    // Extract and adapt styles: body → :host, .report-root; :root → :host, :root
    for (const styleEl of doc.head.querySelectorAll("style")) {
      const adapted = document.createElement("style");
      adapted.textContent = (styleEl.textContent ?? "")
        .replace(/\bbody\b(?=\s*\{)/g, ":host, .report-root")
        .replace(/:root\b/g, ":host, :root");
      shadow.appendChild(adapted);
    }

    const darkOverride = document.createElement("style");
    darkOverride.textContent = `
      :host([data-theme="dark"]), .report-root.dark, .report-theme-dark {
        --bg: #141312;
        --bg-page: #141312;
        --card-bg: #1F1D1B;
        --bg-card: #1F1D1B;
        --text: #F4F2EE;
        --text-primary: #F4F2EE;
        --foreground: #F4F2EE;
        --text-secondary: #D4D0C8;
        --muted: #D4D0C8;
        --text-muted: #9E9791;
        --muted-foreground: #D4D0C8;
        --border: rgba(244, 242, 238, 0.14);
        --border-color: rgba(244, 242, 238, 0.14);
        --border-light: rgba(244, 242, 238, 0.08);
        --border-subtle: rgba(244, 242, 238, 0.08);
        color: #F4F2EE;
      }
      .report-root.dark .verdict-reasoning,
      .report-root.dark .prose-quote,
      .report-root.dark .prose-voice,
      .report-root.dark .verdict-item p,
      .report-root.dark .verdict-question {
        color: #F4F2EE !important;
      }
      .report-root.dark .finding-meta,
      .report-root.dark .metadata-bar {
        color: #D4D0C8 !important;
      }
    `;
    shadow.appendChild(darkOverride);

    const isDark = document.documentElement.classList.contains("dark");
    if (isDark) {
      container.setAttribute("data-theme", "dark");
    } else {
      container.removeAttribute("data-theme");
    }

    // Inject body content inside a themed wrapper
    const wrapper = document.createElement("div");
    wrapper.className = isDark
      ? "report-root dark report-theme-dark"
      : "report-root";
    wrapper.innerHTML = doc.body.innerHTML;
    shadow.appendChild(wrapper);

    // Observe theme toggles on documentElement
    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, [html]);

  return (
    <div
      ref={containerRef}
      className="w-full rounded-lg overflow-hidden border border-border/70 bg-card shadow-xs"
    />
  );
}
